// Libs
import classNames from 'classnames/bind';
import { Box, Grid2, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseLabelValueField, BaseMap, Loading } from '~/components/common';
// Others
import { IClassifyAddresses, ICustomerDetail } from '~/utils/interface/customer';
import { DEFAULT_GG_MAP_LOAD_SCRIPT_LIB, DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { googleMapApiKey } from '~/utils/constants/env';
import { formatPhoneNumber, formatTitleCase, getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getCustomerDetail } from '~/thunks/customer/customerThunk';
// Styles, images, icons
import styles from './CustomerInformation.module.scss';

type Props = {};

const cx = classNames.bind(styles);

// const additionalContactsColumns = (t: TFunction): IHeaderCellType<IAdditionalContact>[] => {
//   return [
//     {
//       id: 'name',
//       label: t('table_head_title_name'),
//       render: (record: IAdditionalContact) => record?.name || EMPTY_STRING,
//     },
//     {
//       id: 'email',
//       label: t('table_head_title_email'),
//       render: (record: IAdditionalContact) => record?.email || EMPTY_STRING,
//     },
//     {
//       id: 'phone',
//       label: t('table_head_title_phone'),
//       render: (record: IAdditionalContact) => {
//         return formatPhoneNumber(record?.phoneNumber);
//       },
//     },
//   ];
// };

// const addressColumns = (t: TFunction): IHeaderCellType<IAddressCustomer>[] => {
//   return [
//     {
//       id: 'address',
//       label: t('table_detail_head_title_address'),
//       render: (record: IAddressCustomer) => record?.address || EMPTY_STRING,
//       width: 200,
//     },
//     {
//       id: 'address2',
//       label: t('table_detail_head_title_second_address'),
//       render: (record: IAddressCustomer) => record?.address2 || EMPTY_STRING,
//       width: 200,
//     },
//     {
//       id: 'city',
//       label: t('table_detail_head_title_city'),
//       render: (record: IAddressCustomer) => record?.city || EMPTY_STRING,
//     },
//     {
//       id: 'state',
//       label: t('table_detail_head_title_state'),
//       render: (record: IAddressCustomer) => record?.state || EMPTY_STRING,
//     },
//     {
//       id: 'zipCode',
//       label: t('table_detail_head_title_zip_code'),
//       render: (record: IAddressCustomer) => record?.zipCode || EMPTY_STRING,
//       width: 100,
//     },
//     {
//       id: 'country',
//       label: t('table_detail_head_title_country'),
//       render: (record: IAddressCustomer) => record?.country || EMPTY_STRING,
//     },
//   ];
// };

const CustomerInformation = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/customer']);
  const { id: customerId } = useParams();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapApiKey ?? EMPTY_STRING,
    libraries: DEFAULT_GG_MAP_LOAD_SCRIPT_LIB,
  });
  const dispatch = useAppDispatch();

  const handleClassifyAddresses = (): IClassifyAddresses => {
    const addresses = customerDetail?.addresses || [];

    return addresses.reduce<IClassifyAddresses>(
      (acc, address) => {
        if (address.isBillingAddress) {
          if (!acc.billingAddress) {
            acc.billingAddress = address;
          }
        } else {
          acc.shippingAddresses.push(address);
        }
        return acc;
      },
      { billingAddress: undefined, shippingAddresses: [] }
    );
  };
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [customerDetail, setCustomerDetail] = useState<ICustomerDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { billingAddress, shippingAddresses } = handleClassifyAddresses();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!customerId) return;
    handleGetCustomerDetail(customerId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetCustomerDetail = (customerId: string) => {
    setIsLoading(true);

    dispatch(getCustomerDetail(customerId))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        setCustomerDetail(data);
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const formatDataContacts =
    customerDetail?.customerContacts &&
    customerDetail.customerContacts.map((item) => {
      const name = item.name || EMPTY_STRING;
      const email = item.email || EMPTY_STRING;
      const phone = formatPhoneNumber(item.phoneNumber) || EMPTY_STRING;

      const nonEmptyValues = [name, email, phone].filter((value) => value !== EMPTY_STRING);

      return nonEmptyValues.join(', ');
    });
  //#endregion Handle Function

  return (
    <Box component={'div'} id='customerInformationPage' className={cx('container')}>
      <Grid2 container spacing={'24px'}>
        <Grid2 size={7}>
          <BaseLabelValueField
            label={t('tab_information_customer')}
            value={getFullName({ firstName: customerDetail?.firstName, lastName: customerDetail?.lastName })}
          />

          <BaseLabelValueField
            label={t('tab_information_email')}
            value={customerDetail?.email || EMPTY_STRING}
          />

          <BaseLabelValueField
            label={t('tab_information_phone')}
            value={formatPhoneNumber(customerDetail?.phoneNumber) || EMPTY_STRING}
          />

          <BaseLabelValueField
            label={t('tab_information_tags')}
            value={customerDetail?.tag ? formatTitleCase(customerDetail?.tag) : EMPTY_STRING}
          />

          <BaseLabelValueField
            label={t('tab_information_status_quick_books')}
            value={
              <Box
                component={'div'}
                className={cx('quickBooks', { connected: customerDetail?.quickBookCustomerId })}
              ></Box>
            }
          />

          <BaseLabelValueField
            label={t('tab_information_additional_contacts')}
            value={
              <Box component={'div'} className={cx('additionalContacts')}>
                {formatDataContacts &&
                  formatDataContacts.length > DEFAULT_NUMBER_ZERO &&
                  formatDataContacts.map((contact, index) => (
                    <Typography component={'div'} key={index} className={cx('contact')}>
                      {contact}
                    </Typography>
                  ))}
              </Box>
            }
          />

          {/*<Box component={'div'} className={cx('additionalContacts')}>*/}
          {/*  <Tooltip*/}
          {/*    title={t('tab_information_additional_contacts')}*/}
          {/*    placement='top-start'*/}
          {/*    enterDelay={DEFAULT_TOOLTIP_DELAY}*/}
          {/*    leaveDelay={DEFAULT_TOOLTIP_DELAY}*/}
          {/*  >*/}
          {/*    <Typography component={'div'} className={cx('additionalContactsTitle')}>*/}
          {/*      {t('tab_information_additional_contacts')}*/}
          {/*    </Typography>*/}
          {/*  </Tooltip>*/}

          {/*  <BaseTable*/}
          {/*    columns={additionalContactsColumns(t)}*/}
          {/*    dataSource={customerDetail?.customerContacts || []}*/}
          {/*  />*/}
          {/*</Box>*/}

          {/*<Box component={'div'} className={cx('additionalContacts')}>*/}
          {/*  <Tooltip*/}
          {/*    title={t('tab_information_addresses')}*/}
          {/*    placement='top-start'*/}
          {/*    enterDelay={DEFAULT_TOOLTIP_DELAY}*/}
          {/*    leaveDelay={DEFAULT_TOOLTIP_DELAY}*/}
          {/*  >*/}
          {/*    <Typography component={'div'} className={cx('additionalContactsTitle')}>*/}
          {/*      {t('tab_information_addresses')}*/}
          {/*    </Typography>*/}
          {/*  </Tooltip>*/}

          {/*  <BaseTable columns={addressColumns(t)} dataSource={customerDetail?.addresses || []} />*/}
          {/*</Box>*/}

          {/* <BaseLabelValueField
            label={t('tab_information_primary_contact')}
            value={
              <Stack spacing={'8px'}>
                <Typography component={'p'} className={cx('textValue')}>
                  {customerDetail?.primaryContact?.name || EMPTY_STRING}
                </Typography>

                <Typography component={'p'} className={cx('textValue')}>
                  {formatPhoneNumber(customerDetail?.primaryContact?.phoneNumber)}
                </Typography>

                <Typography component={'p'} className={cx('textValue')}>
                  {customerDetail?.primaryContact?.email || EMPTY_STRING}
                </Typography>
              </Stack>
            }
          />

          <BaseLabelValueField
            label={t('tab_information_service_location')}
            value={getFullAddress({ ...customerDetail?.serviceLocation })}
          />

          <BaseLabelValueField
            label={t('tab_information_job_description')}
            value={customerDetail?.jobDescription || EMPTY_STRING}
          />

          <BaseLabelValueField label={t('tab_information_po')} value={customerDetail?.po || EMPTY_STRING} />

          <BaseLabelValueField
            label={t('tab_information_job_category')}
            value={customerDetail?.jobCategory || EMPTY_STRING}
          />

          <BaseLabelValueField
            label={t('tab_information_job_source')}
            value={customerDetail?.jobSource || EMPTY_STRING}
          />

          <BaseLabelValueField
            label={t('tab_information_agent_rep')}
            value={customerDetail?.agentRep || EMPTY_STRING}
          /> */}
        </Grid2>

        <Grid2 container direction={'row'} size={5} spacing={0}>
          <Stack className={cx('mapContainer')}>
            <BaseLabelValueField
              label={t('tab_information_billing_address')}
              value={billingAddress?.address}
              bordered={false}
              labelSize={'auto'}
            />

            <Stack className={cx('map')}>
              {isLoaded && (
                <BaseMap
                  {...(billingAddress?.latitude &&
                    billingAddress?.longitude && {
                      position: {
                        lat: billingAddress?.latitude as number,
                        lng: billingAddress?.longitude as number,
                      },
                    })}
                />
              )}
            </Stack>
          </Stack>

          {shippingAddresses &&
            shippingAddresses.map((item, index) => {
              return (
                <Stack key={index} className={cx('mapContainer')}>
                  <BaseLabelValueField
                    label={t('tab_information_addresses')}
                    value={item?.address}
                    bordered={false}
                    labelSize={'auto'}
                  />

                  <Stack className={cx('map')}>
                    {isLoaded && (
                      <BaseMap
                        {...(item?.latitude &&
                          item?.longitude && {
                            position: {
                              lat: item.latitude as number,
                              lng: item.longitude as number,
                            },
                          })}
                      />
                    )}
                  </Stack>
                </Stack>
              );
            })}
        </Grid2>
      </Grid2>

      {isLoading && <Loading />}
    </Box>
  );
};

export default CustomerInformation;
